import type {
  AdapterDeps,
  ConnectOptions,
  ConnectionAuthConfig,
  ServiceAuthAdapter,
  ServiceKey,
  TokenSet,
} from "./types";
import { ServiceAuthError } from "./types";
import { logConnectorFailure, logConnectorSuccess } from "../../telemetry";

// The prototype's Cognito App Clients are configured for `openid` only —
// requesting `email`/`profile` returns `invalid_scope` from the Hosted UI.
// Override per-service via authConfig.scopes if a client allows broader scopes.
const DEFAULT_SCOPES = ["openid"];
const TOKEN_EXPIRY_BUFFER_MS = 60_000;

interface TokenEndpointResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type?: string;
}

interface TokenEndpointError {
  error: string;
  error_description?: string;
}

function isTokenError(payload: unknown): payload is TokenEndpointError {
  return typeof payload === "object" && payload !== null && "error" in payload;
}

/**
 * Generic OAuth code+PKCE Cognito adapter. Subclasses provide:
 *   - the ServiceKey / category
 *   - getAuthHeaders() (each service has its own header shape)
 *   - optionally override silentConnect for service-specific quirks (e.g. iframe SSO)
 *
 * Concurrency guarantees provided here:
 *   1. refresh() is deduplicated via a singleton in-flight promise.
 *   2. silentConnect()/connect() share an acquire-in-flight promise.
 *   3. doRefresh persists refresh_token to localStorage BEFORE updating in-memory
 *      tokens BEFORE firing onTokenChange. invalid_grant => disconnect().
 */
export abstract class CognitoAdapterBase implements ServiceAuthAdapter {
  abstract readonly key: ServiceKey;
  abstract readonly category: string;
  abstract getAuthHeaders(): Record<string, string>;

  protected tokens: TokenSet | null = null;
  protected refreshInFlight: Promise<TokenSet> | null = null;
  protected acquireInFlight: Promise<TokenSet | null> | null = null;
  protected listeners = new Set<(t: TokenSet | null) => void>();

  constructor(
    protected readonly cfg: ConnectionAuthConfig,
    protected readonly deps: AdapterDeps,
  ) {}

  /* ── Public API ────────────────────────────── */

  isConnected(): boolean {
    return this.tokens !== null && this.tokens.expiresAt - TOKEN_EXPIRY_BUFFER_MS > this.now();
  }

  silentConnect(): Promise<TokenSet | null> {
    if (this.isConnected()) return Promise.resolve(this.tokens);
    if (this.acquireInFlight) return this.acquireInFlight;
    this.acquireInFlight = this.doSilentConnect().finally(() => {
      this.acquireInFlight = null;
    });
    return this.acquireInFlight;
  }

  connect(opts?: ConnectOptions): Promise<TokenSet> {
    if (this.acquireInFlight) {
      return this.acquireInFlight.then((tokens) => tokens ?? this.doInteractiveConnect(opts));
    }
    const promise = this.doInteractiveConnect(opts);
    // Single chain absorbs both fulfilment and rejection so the in-flight
    // tracking promise never produces an unhandled rejection.
    this.acquireInFlight = promise
      .then<TokenSet | null>((t) => t)
      .catch((): TokenSet | null => null)
      .finally(() => {
        this.acquireInFlight = null;
      });
    return promise;
  }

  refresh(): Promise<TokenSet> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  disconnect(): void {
    this.tokens = null;
    this.deps.storage.clear(this.key);
    this.emit(null);
  }

  onTokenChange(cb: (t: TokenSet | null) => void): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /* ── Internal flow ─────────────────────────── */

  protected async doSilentConnect(): Promise<TokenSet | null> {
    const refreshToken = this.deps.storage.getRefreshToken(this.key);
    if (!refreshToken) return null;
    try {
      const tokens = await this.doRefresh();
      logConnectorSuccess(this.key, "silent-reconnect");
      return tokens;
    } catch (err) {
      // Refresh failed (likely invalid_grant) — disconnect already cleared state.
      // The connector silently drops to disconnected here; log it so a whole
      // class of "my connector keeps signing me out" reports is diagnosable.
      logConnectorFailure(this.key, err, "silent-reconnect-failed");
      return null;
    }
  }

  protected async doInteractiveConnect(opts?: ConnectOptions): Promise<TokenSet> {
    const verifier = generateCodeVerifier();
    const challenge = await sha256Base64Url(verifier);
    const state = randomString(16);
    const redirectUri = this.redirectUri();
    const authorizeUrl = this.buildAuthorizeUrl({
      challenge,
      state,
      prompt: opts?.prompt,
      redirectUri,
    });

    const result = await this.deps.dialogRunner.run(authorizeUrl, this.redirectOrigin());
    if (!result.ok) {
      throw new ServiceAuthError(this.key, result.description ?? result.error);
    }
    if (result.state !== undefined && result.state !== state) {
      throw new ServiceAuthError(this.key, "state_mismatch");
    }

    const tokens = await this.exchangeCode(result.code, verifier, redirectUri);
    return tokens;
  }

  protected async doRefresh(): Promise<TokenSet> {
    const refreshToken = this.deps.storage.getRefreshToken(this.key);
    if (!refreshToken) {
      throw new ServiceAuthError(this.key, "no_refresh_token");
    }
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: this.cfg.clientId,
      refresh_token: refreshToken,
    });
    return this.postToken(body, refreshToken);
  }

  protected async exchangeCode(
    code: string,
    verifier: string,
    redirectUri: string,
  ): Promise<TokenSet> {
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.cfg.clientId,
      code,
      code_verifier: verifier,
      redirect_uri: redirectUri,
    });
    return this.postToken(body, null);
  }

  protected async postToken(
    body: URLSearchParams,
    previousRefreshToken: string | null,
  ): Promise<TokenSet> {
    const fetchImpl = this.deps.fetchImpl ?? fetch;
    const res = await fetchImpl(this.tokenEndpoint(), {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    const payload: unknown = await res.json().catch(() => ({}));

    if (!res.ok || isTokenError(payload)) {
      const err = isTokenError(payload) ? payload : { error: `http_${res.status}` };
      if (err.error === "invalid_grant") {
        this.disconnect();
      }
      throw new ServiceAuthError(this.key, err.error_description ?? err.error);
    }

    const ok = payload as TokenEndpointResponse;
    // Persist BEFORE updating in-memory state so a crash between leaves localStorage authoritative.
    const newRefresh = ok.refresh_token ?? previousRefreshToken;
    if (newRefresh) {
      this.deps.storage.setRefreshToken(this.key, newRefresh);
    }
    const tokens: TokenSet = {
      accessToken: ok.access_token,
      idToken: ok.id_token,
      expiresAt: this.now() + ok.expires_in * 1000,
    };
    this.tokens = tokens;
    this.emit(tokens);
    return tokens;
  }

  /* ── Helpers ───────────────────────────────── */

  protected emit(tokens: TokenSet | null): void {
    for (const cb of this.listeners) {
      try {
        cb(tokens);
      } catch {
        // Listener errors are isolated.
      }
    }
  }

  protected tokenEndpoint(): string {
    if (this.cfg.tokenEndpoint) {
      if (this.cfg.tokenEndpoint.startsWith("/")) {
        return this.redirectOrigin() + this.cfg.tokenEndpoint;
      }
      return this.cfg.tokenEndpoint;
    }
    return `https://${this.cfg.hostedUiDomain}/oauth2/token`;
  }

  protected redirectUri(): string {
    return `${this.redirectOrigin()}/auth-dialog-cognito.html`;
  }

  protected redirectOrigin(): string {
    return globalThis.window?.location.origin ?? "";
  }

  protected buildAuthorizeUrl(args: {
    challenge: string;
    state: string;
    prompt?: NonNullable<ConnectOptions["prompt"]>;
    redirectUri: string;
  }): string {
    const url = new URL(`https://${this.cfg.hostedUiDomain}/oauth2/authorize`);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.cfg.clientId);
    url.searchParams.set("redirect_uri", args.redirectUri);
    url.searchParams.set("scope", (this.cfg.scopes ?? DEFAULT_SCOPES).join(" "));
    url.searchParams.set("code_challenge", args.challenge);
    url.searchParams.set("code_challenge_method", "S256");
    url.searchParams.set("state", args.state);
    if (args.prompt) url.searchParams.set("prompt", args.prompt);
    if (this.cfg.identityProvider) url.searchParams.set("identity_provider", this.cfg.identityProvider);
    if (this.cfg.idpIdentifier) url.searchParams.set("idp_identifier", this.cfg.idpIdentifier);
    return url.toString();
  }

  protected now(): number {
    return this.deps.now ? this.deps.now() : Date.now();
  }
}

/* ── PKCE helpers ──────────────────────────── */

function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  crypto.getRandomValues(out);
  return out;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const byte of bytes) {
    s += String.fromCodePoint(byte);
  }
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").split("=").join("");
}

export function generateCodeVerifier(): string {
  return base64UrlEncode(randomBytes(32));
}

export async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

export function randomString(length: number): string {
  return base64UrlEncode(randomBytes(length)).slice(0, length);
}
